import { createContext, use, useMemo, ReactNode } from 'react';
import { useAccounts } from './hooks/useAccounts';
import { useTrackAccountsAdded } from '../creation/hooks/useTrackAccountsAdded';
import { Account } from '../types';

interface AccountsContextValue {
  accounts: Account[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

const AccountsContext = createContext<AccountsContextValue | null>(null);

interface AccountsProviderProps {
  children: ReactNode;
}

export function AccountsProvider({ children }: AccountsProviderProps) {
  const { data: accounts = [], isLoading, error, refetch } = useAccounts();

  useTrackAccountsAdded(accounts);

  const value = useMemo<AccountsContextValue>(() => ({
    accounts,
    isLoading,
    error: error ?? null,
    refetch: () => { void refetch(); },
  }), [accounts, isLoading, error, refetch]);

  return (
    <AccountsContext value={value}>
      {children}
    </AccountsContext>
  );
}

export function useAccountsContext() {
  const context = use(AccountsContext);
  if (!context) {
    throw new Error('useAccountsContext must be used within an AccountsProvider');
  }
  return context;
}
